(function () {

  /* ============================================
     CONFLICT DATA — from Fault_Lines_Conflicts_2026.xlsx
     Rows marked "Y" or "Internal" in the Render column only.
     Source of truth for edges: "Confirmed Borders" column —
     first segment = a (Base), second = b (Top), third (if
     present) = c (diverging 3-way). Single segment = internal
     (twisting on its own border) -> only "a" is set, no "b".

     a/b/c are now CODES (e.g. "RUSSIA"), not display text —
     the actual translated country name is looked up from
     T.countries at render time (see countryLabel() below).
     "name" stays as English text AND doubles as the guaranteed
     fallback if a translated conflict name is ever missing;
     T.conflictNames[handle] is preferred when present.

     "year" = researched start date of the phase most relevant to
     the Confirmed Borders framing. For genuinely ambiguous cases
     (real historical origin vs. current acute phase), format is
     "ORIGIN // CURRENT PHASE" rather than picking one arbitrarily.
     Two entries (brazil, usa-china... see below) are left single-
     year because they are continuous phenomena with no distinct
     second flare-up point — a "//" there would invent a date that
     doesn't correspond to a real event.

     Renamed since last round (no export files exist yet, so this
     costs nothing now and prevents permanently-wrong filenames):
     - "israel-gaza-westbank" -> "israel-palestine". Also
       restructured from a 3-edge diverging entry (Israel/Gaza/
       West Bank) to a standard 2-edge entry (Israel/Palestine),
       since Gaza and West Bank can't both be labeled "Palestine"
       without duplicating the tile label. FLAG: confirm this
       still matches the intended vase geometry (was designed as
       a 3-profile diverging form; is now conceptually 2-profile).
     - "haiti-dominica" -> "haiti-dominican-republic", correcting
       the country itself (Dominica and the Dominican Republic are
       different countries).

     Thumbnail path convention (from the export script, Part 1):
       {handle}.png -> uploaded to Shopify Files, referenced via
       THUMB_BASE below. Update THUMB_BASE to the real Shopify
       Files URL prefix once the export batch is uploaded.
     ============================================ */
  const THUMB_BASE = "https://cdn.shopify.com/s/files/1/0726/7221/5128/files/";
  const THUMB_SUFFIX = ".png";

  /* Real uploaded filenames don't match our clean handles (they're
     named by rank + original country spelling from the spreadsheet,
     e.g. "faultlines_R01_Russia-Ukraine.png"). Rather than rename 70
     files in Shopify (no bulk/in-place rename exists there), this
     map translates handle -> actual filename (no extension). Update
     this map only if a filename is ever changed in Shopify Files. */
  const FILE_MAP = {
    "russia-ukraine":              "faultlines_R01_Russia-Ukraine",
    "ethiopia-eritrea":            "faultlines_R02_Ethiopia-Eritrea",
    "yemen":                       "faultlines_R03_Yemen",
    "sudan":                       "faultlines_R04_Sudan",
    "syria":                       "faultlines_R05_Syria",
    "pakistan-afghanistan":        "faultlines_R06_Pakistan-Afghanistan",
    "mexico":                      "faultlines_R07_Mexico",
    "israel-palestine":            "faultlines_R09_Israel-Gaza-WestBank",
    "nigeria":                     "faultlines_R10_Nigeria",
    "somalia":                     "faultlines_R11_Somalia",
    "myanmar":                     "faultlines_R12_Myanmar",
    "congo-rwanda":                "faultlines_R13_Congo-Rwanda",
    "brazil":                      "faultlines_R14_Brazil",
    "burkina":                     "faultlines_R15_Burkina",
    "colombia":                    "faultlines_R16_Colombia",
    "mali":                        "faultlines_R17_Mali",
    "haiti-dominican-republic":    "faultlines_R20_Haiti-Dominica",
    "lebanon-israel":              "faultlines_R22_Lebanon-Israel",
    "cameroon":                    "faultlines_R23_Cameroon",
    "niger":                       "faultlines_R26_Niger",
    "ecuador":                     "faultlines_R28_Equador",
    "mozambique":                  "faultlines_R29_Mozambique",
    "iran-israel":                 "faultlines_R31_Iran-Israel",
    "usa-iran":                    "faultlines_R32_USA-Iran",
    "india-pakistan":              "faultlines_R35_India-Pakistan",
    "usa-venezuela":               "faultlines_R36_USA-Venezuela",
    "cambodia-thailand":           "faultlines_R42_Cambodia-Thailand",
    "serbia-kosovo":               "faultlines_R43_Serbia-Kosovo",
    "south-korea-north-korea":     "faultlines_R44_South-Korea-North-Korea",
    "cyprus-turkiye":              "faultlines_R45_Cyprus-Turkiye",
    "china-taiwan":                "faultlines_R46_China-Taiwan",
    "usa-cuba":                    "faultlines_R48_USA-Cuba",
    "usa-greenland":               "faultlines_R49_USA-Greenland",
    "usa-china":                   "faultlines_R50_USA-China",
    "china-india":                 "faultlines_R51_China-India"
  };

  /* handle -> real uploaded filename, falling back to the handle
     itself (so a missing map entry fails safely as a 404 image
     rather than a JS crash). */
  function fileName(handle) {
    return FILE_MAP[handle] || handle;
  }

  const CONFLICTS = [
    { handle: "russia-ukraine",         a: "RUSSIA",         b: "UKRAINE",        year: "2014 // 2022", name: "Russo-Ukrainian War" },
    { handle: "ethiopia-eritrea",       a: "ETHIOPIA",       b: "ERITREA",        year: "1998", name: "Eritrean-Ethiopian border conflict" },
    { handle: "yemen",                  a: "YEMEN",          internal: true,      year: "2014", name: "Yemeni Civil War" },
    { handle: "sudan",                  a: "SUDAN",          internal: true,      year: "2023", name: "Sudanese Civil War" },
    { handle: "syria",                  a: "SYRIA",          internal: true,      year: "2011 // 2024", name: "Syrian Conflict" },
    { handle: "pakistan-afghanistan",   a: "PAKISTAN",       b: "AFGHANISTAN",    year: "1893 // 2021", name: "Pakistan\u2013Afghanistan border conflict" },
    { handle: "mexico",                 a: "MEXICO",         internal: true,      year: "2006", name: "Mexican Drug War" },
    { handle: "israel-palestine",       a: "ISRAEL",         b: "PALESTINE",      year: "1948 // 2023", name: "Israeli-Palestinian conflict" },
    { handle: "nigeria",                a: "NIGERIA",        internal: true,      year: "2009", name: "Boko Haram insurgency" },
    { handle: "somalia",                a: "SOMALIA",        internal: true,      year: "1991", name: "Somali Civil War" },
    { handle: "myanmar",                a: "MYANMAR",        internal: true,      year: "1948 // 2021", name: "Myanmar Civil War" },
    { handle: "congo-rwanda",           a: "CONGO",          b: "RWANDA",         year: "2022", name: "Eastern DR Congo conflict (M23)" },
    { handle: "brazil",                 a: "BRAZIL",         internal: true,      year: "2006", name: "Brazilian Drug War" },
    { handle: "burkina",                a: "BURKINA_FASO",   internal: true,      year: "2015", name: "Sahel insurgency (Burkina Faso)" },
    { handle: "colombia",               a: "COLOMBIA",       internal: true,      year: "1964", name: "Colombian armed conflict" },
    { handle: "mali",                   a: "MALI",           internal: true,      year: "2012", name: "Sahel insurgency (Mali)" },
    { handle: "haiti-dominican-republic", a: "HAITI",        b: "DOMINICAN_REPUBLIC", year: "2021", name: "Haitian gang crisis" },
    { handle: "lebanon-israel",         a: "LEBANON",        b: "ISRAEL",         year: "1982 // 2024", name: "Israel\u2013Hezbollah conflict" },
    { handle: "cameroon",               a: "CAMEROON",       internal: true,      year: "2016", name: "Anglophone Crisis" },
    { handle: "niger",                  a: "NIGER",          internal: true,      year: "2015", name: "Sahel insurgency (Niger)" },
    { handle: "ecuador",                a: "ECUADOR",        internal: true,      year: "2021 // 2024", name: "Ecuadorian narco-insurgency" },
    { handle: "mozambique",             a: "MOZAMBIQUE",     internal: true,      year: "2017", name: "Cabo Delgado insurgency" },
    { handle: "iran-israel",            a: "IRAN",           b: "ISRAEL",         year: "2024 // 2026", name: "Iran\u2013Israel conflict" },
    { handle: "usa-iran",               a: "UNITED_STATES",  b: "IRAN",           year: "1979 // 2026", name: "US\u2013Iran conflict" },
    { handle: "india-pakistan",         a: "INDIA",          b: "PAKISTAN",       year: "1947 // 2025", name: "Kashmir conflict" },
    { handle: "usa-venezuela",          a: "UNITED_STATES",  b: "VENEZUELA",      year: "1999 // 2026", name: "US\u2013Venezuela crisis" },
    { handle: "cambodia-thailand",      a: "CAMBODIA",       b: "THAILAND",       year: "2025", name: "Cambodian\u2013Thai border conflict" },
    { handle: "serbia-kosovo",          a: "SERBIA",         b: "KOSOVO",         year: "1998 // 2023", name: "Serbia\u2013Kosovo dispute" },
    { handle: "south-korea-north-korea", a: "SOUTH_KOREA",   b: "NORTH_KOREA",    year: "1950", name: "Korean conflict" },
    { handle: "cyprus-turkiye",         a: "CYPRUS",         b: "TURKIYE",        year: "1974", name: "Cyprus dispute" },
    { handle: "china-taiwan",           a: "CHINA",          b: "TAIWAN",         year: "1949 // 2022", name: "Cross-strait tension" },
    { handle: "usa-cuba",               a: "UNITED_STATES",  b: "CUBA",           year: "1959", name: "US\u2013Cuba tension" },
    { handle: "usa-greenland",          a: "UNITED_STATES",  b: "GREENLAND",      year: "2019 // 2025", name: "US\u2013Greenland dispute" },
    { handle: "usa-china",              a: "UNITED_STATES",  b: "CHINA",          year: "1949 // 2018", name: "US\u2013China rivalry" },
    { handle: "china-india",            a: "CHINA",          b: "INDIA",          year: "1962 // 2020", name: "Sino-Indian border dispute" }
  ];

  /* ---------- translated strings (JSON script tag from Liquid) ---------- */
  function loadStrings() {
    const fallback = {
      heroLine1: "NOTHING RESOLVES.",
      heroLine2: "NOTHING RECONCILES.",
      teaser: "Two borders. Cast as one.",
      contextLine: "{{n}} of 50+ world's active conflicts. Traced at their borders. Recast as vase objects.",
      edgeLabel: "EDGE",
      closingLine1: "The lines keep changing.",
      closingLine2: "The reason never does.",
      positionLine: "Real borders, not drawn here and not resolved here.",
      manifesto1: "Not neutral.",
      manifesto2: "Never neutral.",
      persistentCta: "PICK YOUR SIDE \u2192",
      productUrl: "/products/fault-lines",
      countries: {},
      conflictNames: {}
    };
    const el = document.getElementById("fault-lines-data");
    if (!el) return fallback;
    try {
      return Object.assign({}, fallback, JSON.parse(el.textContent));
    } catch (e) {
      return fallback;
    }
  }

  /* code (e.g. "RUSSIA") -> translated display name.
     Falls back to the code itself, which is already valid
     English display text, if a translation key is missing. */
  function countryLabel(code) {
    if (!code) return code;
    return (T.countries && T.countries[code]) || code;
  }

  /* handle -> translated conflict name, falling back to the
     English "name" field carried in CONFLICTS itself. */
  function conflictName(c) {
    return (T.conflictNames && T.conflictNames[c.handle]) || c.name;
  }

  const T = loadStrings();

  /* ---------- patina overlay, injected on <body> ----------
     Attaching to <body> bypasses the theme's display:grid body and
     Dawn's cascade, which otherwise collapse an in-template fixed div
     to 0x0 / display:none. Verified working. */
  function injectPatina() {
    if (document.getElementById("fl-patina-scan")) return;

    var scan = document.createElement("div");
    scan.id = "fl-patina-scan";
    scan.setAttribute("aria-hidden", "true");
    scan.style.cssText = [
      "position:fixed", "top:0", "left:0", "right:0", "bottom:0",
      "width:100vw", "height:100vh",
      "pointer-events:none", "z-index:9998", "display:block",
      "background:repeating-linear-gradient(0deg," +
        "rgba(237,232,220,0.10) 0px," +
        "rgba(237,232,220,0.10) 1px," +
        "rgba(237,232,220,0) 1px," +
        "rgba(237,232,220,0) 3px)",
      "animation:flScanBody 3s linear infinite"
    ].join(";");

    var vignette = document.createElement("div");
    vignette.id = "fl-patina-vignette";
    vignette.setAttribute("aria-hidden", "true");
    vignette.style.cssText = [
      "position:fixed", "top:0", "left:0", "right:0", "bottom:0",
      "width:100vw", "height:100vh",
      "pointer-events:none", "z-index:9997", "display:block",
      "box-shadow:inset 0 0 240px 60px rgba(0,0,0,0.9)"
    ].join(";");

    if (!document.getElementById("fl-patina-style")) {
      var style = document.createElement("style");
      style.id = "fl-patina-style";
      style.textContent =
        "@keyframes flScanBody { 0% { background-position: 0 0; } 100% { background-position: 0 90px; } }";
      document.head.appendChild(style);
    }

    document.body.appendChild(vignette);
    document.body.appendChild(scan);
  }

  /* ---------- text zones ---------- */
  function renderText() {
    const n = CONFLICTS.length;

    setText("flHeroLine1", T.heroLine1);
    setText("flHeroLine2", T.heroLine2);
    setText("flTeaser", T.teaser);
    const contextLineEl = document.getElementById("flContextLine");
    if (contextLineEl) {
      const countHtml = '<span class="fl-context-count">' + n + '</span>';
      contextLineEl.innerHTML = T.contextLine.replace("{{n}}", countHtml);
    }
    setText("flClosingLine1", T.closingLine1);
    setText("flClosingLine2", T.closingLine2);
    setText("flPositionLine", T.positionLine);
    setText("flPositionManifesto", (T.manifesto1 + " " + T.manifesto2));
    setText("flPositionCta", T.persistentCta);
  }

  function setText(id, value) {
    const el = document.getElementById(id);
    if (el) el.textContent = value;
  }

  /* ---------- tile grid ---------- */
  function renderGrid() {
    const grid = document.getElementById("flGrid");
    if (!grid) return;
    const frag = document.createDocumentFragment();

    /* Tile order: alphabetical by first country (a), sorted on the
       TRANSLATED name so the order is correct per-language, not just
       in English. A sorted copy — CONFLICTS itself is never mutated. */
    const sorted = CONFLICTS.slice().sort(function (x, y) {
      return countryLabel(x.a).localeCompare(countryLabel(y.a));
    });

    sorted.forEach(function (c) {
      const tile = document.createElement("a");
      tile.className = "fl-tile";
      tile.href = T.productUrl + "?conflict=" + encodeURIComponent(c.handle);
      tile.setAttribute("data-reveal", "1");

      let edges = '<div class="fl-tile-edge"><span class="fl-edge-prefix">' + T.edgeLabel + ' \u00B7 </span>' + countryLabel(c.a) + '</div>';
      if (!c.internal && c.b) {
        edges += '<div class="fl-tile-edge"><span class="fl-edge-prefix">' + T.edgeLabel + ' \u00B7 </span>' + countryLabel(c.b) + '</div>';
      }
      if (c.c) {
        edges += '<div class="fl-tile-edge"><span class="fl-edge-prefix">' + T.edgeLabel + ' \u00B7 </span>' + countryLabel(c.c) + '</div>';
      }

      tile.innerHTML =
        '<div class="fl-tile-thumb">' +
          '<img src="' + THUMB_BASE + fileName(c.handle) + THUMB_SUFFIX + '" alt="" loading="lazy">' +
        '</div>' +
        '<div class="fl-tile-meta">' +
          edges +
          '<div class="fl-tile-year">' + c.year + '</div>' +
          '<div class="fl-tile-name">' + conflictName(c) + '</div>' +
        '</div>';

      frag.appendChild(tile);
    });

    grid.appendChild(frag);
  }

  /* ---------- reveal on scroll ---------- */
  function initReveal() {
    const items = document.querySelectorAll("[data-reveal]");
    if (!("IntersectionObserver" in window)) {
      items.forEach(function (i) { i.classList.add("fl-revealed"); });
      return;
    }
    const io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add("fl-revealed");
          io.unobserve(entry.target);
        }
      });
    }, { threshold: 0.12 });
    items.forEach(function (i) { io.observe(i); });
  }

  document.addEventListener("DOMContentLoaded", function () {
    injectPatina();
    renderText();
    renderGrid();
    initReveal();
  });

})();
